import { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useTimeBar } from '@/contexts/TimeBarContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { LayoutDashboard, Settings, LogOut, ChevronLeft, ChevronRight, Search, Users, Building2, FolderKanban, X, Bell, Siren, Loader2, AlertTriangle, FileText, FileSearch, Layers, Sun, Moon, Wrench, HardHat, ChevronsUpDown, Check, Shield, Scale, Timer, Mail, CheckCircle2, Camera, ScanLine, FileDown, Boxes, Sparkles, Crown, CalendarClock, GitMerge, Grid3x3, FileCheck2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import api from '@/lib/api';
import { useProject } from '@/contexts/ProjectContext';
import { PanorixLogo, PanorixLogoStacked } from '@/components/PanorixLogo';
import NotificationCenter from '@/components/NotificationCenter';
import MyActionsTray from '@/components/MyActionsTray';
import ModuleHelp from '@/components/common/ModuleHelp';
import LinkJobsDock from '@/components/common/LinkJobsDock';
import { getModuleKeyFromPath } from '@/lib/routeToModuleKey';

// Icon mapping for dynamic sidebar
const ICON_MAP = {
  'calendar-clock': Layers, 'file-text': FileText, 'shopping-cart': Layers,
  'hard-hat': HardHat, 'check-square': Layers, 'dollar-sign': Layers,
  'grid': Layers, 'box': Layers, 'eye': Layers, 'layers': Layers,
  'shield': Shield, 'alert-triangle': AlertTriangle, 'camera': Camera,
  'wrench': Wrench, 'building': HardHat, 'scan-line': ScanLine,
  'file-down': FileDown, 'bell': Bell, 'file-search': FileSearch,
  'grid-2x2': LayoutDashboard, 'folder-kanban': FolderKanban,
  'users': Users, 'settings': Settings, 'boxes': Boxes,
};

// Fallback hardcoded items (used when sidebar API hasn't loaded yet).
// Mirrors all `sidebarParent: None` modules in foundation_routes.MODULE_REGISTRY_SEED
// so users always see every cross-cutting module even if the dynamic API errors.
const baseNavItems = [
  { to: '/projects', icon: FolderKanban, label: 'Projects' },
  { to: '/project-controls', icon: Layers, label: 'Project Controls', end: true },
  { to: '/project-controls/hse', icon: Shield, label: 'HSE Management' },
  { to: '/project-controls/raid', icon: AlertTriangle, label: 'RAID Log' },
  { to: '/project-controls/dms', icon: FileText, label: 'Documents' },
  { to: '/project-controls/nfa', icon: FileCheck2, label: 'NFA Management' },
  { to: '/project-controls/interfaces', icon: GitMerge, label: 'Interface Management' },
  { to: '/project-controls/site-monitoring', icon: Camera, label: 'Site Monitoring' },
  { to: '/piping-workfront', icon: Wrench, label: 'Piping Workfront' },
  { to: '/structural-workfront', icon: HardHat, label: 'Structural Workfront' },
  { to: '/project-controls/material-tracking', icon: Boxes, label: 'Material Tracking' },
  { to: '/project-controls/scan-to-model', icon: ScanLine, label: 'Scan-to-Model' },
  { to: '/project-controls/progress-reports', icon: FileDown, label: 'Progress Reports' },
  { to: '/ai-analyst', icon: Sparkles, label: 'AI Analyst' },
  { to: '/ceo-briefing', icon: Crown, label: 'CEO Briefing' },
  { to: '/contracts', icon: FileText, label: 'Contracts' },
  { to: '/tenders', icon: FileSearch, label: 'Tenders' },
  { to: '/notifications', icon: Bell, label: 'Notifications' },
];

const adminNavItems = [
  { to: '/users', icon: Users, label: 'Users', adminOnly: true },
  { to: '/org',   icon: Building2, label: 'My Organization', adminOnly: true },
  { to: '/settings', icon: Settings, label: 'Settings' },
];

export function DashboardLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const { user, logout, isAdmin, organization, permissions } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { projects, selectedProject, selectProject, loading: projectsLoading, projectId } = useProject();
  const timeBar = useTimeBar();
  const navigate = useNavigate();
  const location = useLocation();
  const [dynamicSidebar, setDynamicSidebar] = useState(null);
  const [fullAccess, setFullAccess] = useState(true);

  // Fetch dynamic sidebar based on selected project
  useEffect(() => {
    if (!projectId || !user) { setDynamicSidebar(null); return; }
    const showPortfolio = localStorage.getItem('ciq_show_portfolio') === 'true';
    api.get(`/projects/${projectId}/sidebar`, { headers: { 'X-Show-Portfolio': showPortfolio ? 'true' : 'false' } })
      .then(res => {
        setDynamicSidebar(res.data?.sidebar || null);
        setFullAccess(res.data?.isFullAccess !== false);
      })
      .catch(() => setDynamicSidebar(null));
  }, [projectId, user]);

  useEffect(() => {
    setShowSearchResults(false);
    setSearchQuery('');
  }, [location.pathname]);

  const performSearch = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    setIsSearching(true);
    try {
      const [portfoliosRes, schedulingRes] = await Promise.all([
        api.get('/portfolios').catch(() => ({ data: [] })),
        api.get('/scheduling/projects').catch(() => ({ data: [] }))
      ]);
      const lowerQuery = query.toLowerCase();
      let projectResults = [];
      if (portfoliosRes.data && portfoliosRes.data.length > 0) {
        for (const portfolio of portfoliosRes.data) {
          try {
            const gridRes = await api.get(`/portfolios/${portfolio.portfolio_id}/grid`);
            const projects = gridRes.data?.projects || [];
            const filtered = projects
              .filter(p => p.name?.toLowerCase().includes(lowerQuery) || p.sector?.toLowerCase().includes(lowerQuery) || p.location?.toLowerCase().includes(lowerQuery) || (typeof p.client === 'object' ? p.client?.clientName : p.client)?.toLowerCase().includes(lowerQuery))
              .slice(0, 5)
              .map(p => ({ id: p.id, name: p.name, type: 'project', subtitle: (typeof p.client === 'object' ? p.client?.clientName : p.client) || p.sector || p.location || 'Project', route: `/projects/${p.id}` }));
            projectResults = [...projectResults, ...filtered];
          } catch (err) { /* skip */ }
        }
      }
      const schedulingResults = (schedulingRes.data || [])
        .filter(p => p.name?.toLowerCase().includes(lowerQuery) || p.description?.toLowerCase().includes(lowerQuery))
        .slice(0, 5)
        .map(p => ({ id: p.id, name: p.name, type: 'scheduling', subtitle: p.description || 'Schedule Project', route: `/scheduling/${p.id}` }));
      const allResults = [...projectResults.slice(0, 5), ...schedulingResults.slice(0, 5)];
      setSearchResults(allResults);
      setShowSearchResults(true);
    } catch (err) { /* skip */ } finally { setIsSearching(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { performSearch(searchQuery); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, performSearch]);

  const handleSearchResultClick = (result) => { navigate(result.route); setShowSearchResults(false); setSearchQuery(''); };
  const handleLogout = async () => { await logout(); navigate('/'); };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  const isTrial = user?.platformRole === 'TRIAL_USER';
  const showAdminItems = !isTrial && (isAdmin?.() || permissions?.role === 'admin');
  // Hide the Settings tile for trial users — they may explore every other module.
  const filteredAdminItems = adminNavItems
    .filter(item => !(isTrial && item.to === '/settings'))
    .filter(item => { if (item.adminOnly) return showAdminItems; return true; });

  // Build navItems from dynamic sidebar API or fallback to static
  const showPortfolio = localStorage.getItem('ciq_show_portfolio') === 'true';
  const navItems = (() => {
    if (dynamicSidebar && dynamicSidebar.length > 0) {
      const items = [];
      for (const item of dynamicSidebar) {
        if (['USERS', 'SETTINGS'].includes(item.moduleCode)) continue;
        // Don't flatten children — Project Controls sub-modules are accessed via their tiles page
        items.push({
          to: item.path,
          icon: ICON_MAP[item.icon] || Layers,
          label: item.label,
          end: item.moduleCode === 'PROJECT_CONTROLS',
        });
      }
      // AI Analyst and CEO Briefing read across every module, so they are only
      // appended for full-access accounts. Appending them unconditionally put
      // them in the sidebar of users restricted to a single module.
      if (fullAccess) {
        if (!items.find(i => i.to === '/ai-analyst')) {
          items.push({ to: '/ai-analyst', icon: Sparkles, label: 'AI Analyst' });
        }
        if (!items.find(i => i.to === '/ceo-briefing')) {
          items.push({ to: '/ceo-briefing', icon: Crown, label: 'CEO Briefing' });
        }
      }
      return items;
    }
    // Fallback to hardcoded
    return showPortfolio
      ? [{ to: '/portfolio', icon: LayoutDashboard, label: 'Portfolio' }, ...baseNavItems]
      : baseNavItems;
  })();

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: 'var(--app-bg)' }}>
      {/* Sidebar — light, wider */}
      <motion.aside
        animate={{ width: collapsed ? 68 : 260 }}
        transition={{ duration: 0.22 }}
        className="fixed left-0 top-0 bottom-0 z-30 flex flex-col"
        style={{ backgroundColor: 'var(--sidebar-bg)', borderRight: '1px solid var(--border-color)' }}
      >
        <div className="flex items-center justify-center py-4 shrink-0"
             style={{ borderBottom: '1px solid var(--border-color)' }}>
          {collapsed ? (
            <button data-testid="sidebar-logo-back" onClick={() => navigate('/launcher')} title="Back to module launcher">
              <PanorixLogo width={48} height={48} showWordmark={false} />
            </button>
          ) : (
            <button data-testid="sidebar-logo-back" onClick={() => navigate('/launcher')}
              title="Back to module launcher"
              className="flex justify-center w-full px-3 transition-transform hover:scale-[1.02]">
              <PanorixLogoStacked logomarkSize={230} />
            </button>
          )}
        </div>

        <div className="px-2 pt-3">
          <button data-testid="sidebar-back-to-launcher" onClick={() => navigate('/launcher')}
            className="group w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, rgba(242,107,31,0.10), rgba(30,78,140,0.08))',
                     border: '1px solid rgba(242,107,31,0.28)', color: '#F26B1F' }}
            title="Back to module launcher">
            <Grid3x3 className="shrink-0 transition-transform group-hover:rotate-90 group-hover:scale-110" strokeWidth={1.8} style={{ width: 18, height: 18 }} />
            <AnimatePresence>
              {!collapsed && (
                <motion.span initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -4 }}
                  className="whitespace-nowrap overflow-hidden font-semibold">
                  Modules
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>

        <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto">
          {navItems.map((item, idx) => {
            return (
              <motion.div key={item.to}
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.015, duration: 0.18 }}>
                <NavLink to={item.to} end={!!item.end}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `sidebar-item group relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${isActive ? 'active font-medium' : ''}`
                  }
                  style={({ isActive }) => ({
                    color: isActive ? 'var(--accent-text)' : 'var(--text-muted)',
                    backgroundColor: isActive ? 'var(--active-bg)' : 'transparent',
                  })}
                >
                  {({ isActive }) => (
                    <>
                      {isActive && (
                        <motion.span layoutId="dash-nav-indicator"
                          className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r"
                          style={{ backgroundColor: 'var(--accent)' }}
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }} />
                      )}
                      <item.icon className="shrink-0 group-hover:scale-110 transition-transform duration-200"
                        strokeWidth={1.6} style={{ width: 18, height: 18 }} />
                      <AnimatePresence>
                        {!collapsed && (
                          <motion.span initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -4 }}
                            className="whitespace-nowrap overflow-hidden flex-1">
                            {item.label}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </NavLink>
              </motion.div>
            );
          })}
          <div className="my-3" style={{ borderTop: '1px solid var(--border-color)' }} />
          {filteredAdminItems.map((item, idx) => (
            <motion.div key={item.to}
              initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}
              transition={{ delay: (navItems.length + idx) * 0.015, duration: 0.18 }}>
              <NavLink to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `sidebar-item group relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${isActive ? 'active font-medium' : ''}`
                }
                style={({ isActive }) => ({
                  color: isActive ? 'var(--accent-text)' : 'var(--text-muted)',
                  backgroundColor: isActive ? 'var(--active-bg)' : 'transparent',
                })}
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span layoutId="dash-nav-indicator"
                        className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r"
                        style={{ backgroundColor: 'var(--accent)' }}
                        transition={{ type: 'spring', stiffness: 500, damping: 30 }} />
                    )}
                    <item.icon className="shrink-0 group-hover:scale-110 transition-transform duration-200"
                      strokeWidth={1.6} style={{ width: 18, height: 18 }} />
                    <AnimatePresence>
                      {!collapsed && (
                        <motion.span initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -4 }}
                          className="whitespace-nowrap overflow-hidden">
                          {item.label}
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </NavLink>
            </motion.div>
          ))}
        </nav>

        <div className="px-2 pb-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
          <button data-testid="sidebar-toggle" onClick={() => setCollapsed(!collapsed)}
            className="flex items-center justify-center w-full py-2 rounded-md transition-colors duration-150"
            style={{ color: 'var(--text-disabled)' }}>
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </motion.aside>

      {/* Main — F9FAFC */}
      <div className={`flex-1 min-w-0 transition-[margin] duration-200 ${collapsed ? 'ml-[68px]' : 'ml-[260px]'}`}
           style={{ backgroundColor: 'var(--app-bg)' }}>
        {/* Header — light */}
        <header className="sticky top-0 z-20 h-14 backdrop-blur-md flex items-center justify-between px-6"
          style={{ backgroundColor: 'var(--header-bg)', borderBottom: '1px solid var(--border-color)' }}>
          <div className="flex items-center gap-3">
            {user?.platformRole === 'TRIAL_USER' && user?.trialExpiresAt && (
              <TrialBadge expiresAt={user.trialExpiresAt}/>
            )}
            {/* Project Selector */}
            <Popover open={projectPickerOpen} onOpenChange={setProjectPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  data-testid="project-selector"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors hover:opacity-90"
                  style={{ backgroundColor: 'var(--accent-bg)', border: '1px solid var(--border-color)' }}
                >
                  <FolderKanban className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--accent)' }} />
                  <span className="text-xs font-medium max-w-[200px] truncate" style={{ color: 'var(--text-primary)' }}>
                    {projectsLoading ? 'Loading...' : selectedProject?.name || 'Select Project'}
                  </span>
                  <ChevronsUpDown className="w-3 h-3 shrink-0" style={{ color: 'var(--text-disabled)' }} />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-72 p-0" align="start" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                <Command>
                  <CommandInput placeholder="Search projects..." className="text-xs" />
                  <CommandList>
                    <CommandEmpty className="py-4 text-center text-xs" style={{ color: 'var(--text-muted)' }}>No projects found</CommandEmpty>
                    <CommandGroup heading="Projects">
                      {projects.map((p) => (
                        <CommandItem
                          key={p.project_id}
                          value={p.name}
                          onSelect={() => { selectProject(p.project_id); setProjectPickerOpen(false); }}
                          className="flex items-center gap-2 text-xs cursor-pointer"
                          data-testid={`project-option-${p.project_id}`}
                        >
                          <Check className={`w-3 h-3 shrink-0 ${selectedProject?.project_id === p.project_id ? 'opacity-100' : 'opacity-0'}`}
                            style={{ color: 'var(--accent)' }} />
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium" style={{ color: 'var(--text-primary)' }}>{p.name}</div>
                            <div className="truncate" style={{ color: 'var(--text-disabled)' }}>{p.project_id} &middot; {typeof p.client === 'object' ? p.client?.clientName : p.client || p.type || ''}</div>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            {organization?.name && (
              <div className="hidden md:flex items-center gap-2 px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--accent-bg)' }}>
                <Building2 className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />
                <span className="text-xs font-medium" style={{ color: 'var(--accent-text)' }}>{organization.name}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 z-10" style={{ color: 'var(--text-disabled)' }} />
              {isSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin z-10" style={{ color: 'var(--text-muted)' }} />}
              <input data-testid="global-search" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.length >= 2 && setShowSearchResults(true)}
                placeholder="Search projects..."
                className="rounded-md pl-9 pr-8 py-1.5 text-xs focus:outline-none w-52 transition-colors duration-150"
                style={{
                  backgroundColor: 'var(--input-bg)', border: '1px solid var(--border-color)',
                  color: 'var(--text-primary)', '::placeholder': { color: 'var(--text-disabled)' },
                }}
              />
              <AnimatePresence>
                {showSearchResults && searchResults.length > 0 && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                    className="absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl overflow-hidden z-50"
                    style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
                    <div className="py-1 max-h-80 overflow-y-auto">
                      {searchResults.map((result) => (
                        <button key={`${result.type}-${result.id}`} onClick={() => handleSearchResultClick(result)}
                          className="w-full px-3 py-2 text-left transition-colors flex items-center gap-3"
                          style={{ ':hover': { backgroundColor: 'var(--hover-bg)' } }}>
                          <div className="p-1.5 rounded" style={{ backgroundColor: 'var(--accent-bg)' }}>
                            {result.type === 'scheduling' ? <CalendarClock className="w-3.5 h-3.5" style={{ color: '#143A6B' }} /> : <FolderKanban className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>{result.name}</p>
                            <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{result.subtitle}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {showSearchResults && <div className="fixed inset-0 z-30" onClick={() => setShowSearchResults(false)} />}

            {/* Theme toggle */}
            <button data-testid="theme-toggle" onClick={toggleTheme}
              className="p-2 rounded-md transition-colors duration-150"
              style={{ color: 'var(--text-muted)' }}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}>
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* General notifications (in-app + email) */}
            <MyActionsTray />
            <NotificationCenter />

            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative p-2 rounded-md transition-colors duration-150 z-40" data-testid="timebar-bell"
                  style={{ color: 'var(--text-muted)' }} title="Time-Bar Alerts (SLA breaches)">
                  <Siren className="w-4 h-4" />
                  {(timeBar?.unreadCount || 0) > 0 && (
                    <span className={`absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-[9px] text-white flex items-center justify-center font-bold ${timeBar?.criticalCount > 0 ? 'bg-red-500 animate-pulse' : 'bg-amber-500'}`}>
                      {timeBar.unreadCount > 9 ? '9+' : timeBar.unreadCount}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-96" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                <div className="px-3 py-2 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <div>
                    <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Time-Bar Alerts</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {timeBar?.connected ? (
                        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 bg-emerald-400 rounded-full inline-block"></span>Live</span>
                      ) : (
                        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full inline-block"></span>Connecting...</span>
                      )}
                    </p>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}>
                    {timeBar?.unreadCount || 0} unread
                  </span>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {(!timeBar?.alerts || timeBar.alerts.length === 0) ? (
                    <div className="px-3 py-8 text-center">
                      <CheckCircle2 className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--text-disabled)' }} />
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No active alerts</p>
                      <p className="text-[10px] mt-1" style={{ color: 'var(--text-disabled)' }}>Time-bar deadlines are being monitored</p>
                    </div>
                  ) : (
                    timeBar.alerts.slice(0, 10).map((alert, i) => {
                      const alertIcon = alert.type === 'CLAIM_NOTICE_ALERT' ? Scale : alert.type === 'LD_CAP_ALERT' ? Timer : alert.type === 'TIME_BAR_ALERT' ? Mail : AlertTriangle;
                      const AlertIcon = alertIcon;
                      const iconColor = alert.severity === 'CRITICAL' ? '#ef4444' : alert.severity === 'HIGH' ? '#f59e0b' : '#3b82f6';
                      return (
                        <div key={alert.id || alert.reference + i} className="px-3 py-2.5 cursor-pointer hover:opacity-80 transition-opacity" style={{ borderLeft: `3px solid ${iconColor}` }}>
                          <div className="flex items-start gap-2">
                            <div className="p-1 rounded flex-shrink-0" style={{ backgroundColor: `${iconColor}20` }}>
                              <AlertIcon className="w-3.5 h-3.5" style={{ color: iconColor }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${alert.severity === 'CRITICAL' ? 'bg-red-500/20 text-red-400' : alert.severity === 'HIGH' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                                  {alert.severity}
                                </span>
                                <span className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>{alert.reference}</span>
                              </div>
                              <p className="text-xs leading-tight" style={{ color: 'var(--text-primary)' }}>{alert.message || alert.subject}</p>
                              <div className="flex items-center justify-between mt-1.5">
                                <p className="text-[10px]" style={{ color: 'var(--text-disabled)' }}>
                                  {alert.days_remaining != null ? (
                                    alert.days_remaining < 0 ? `${Math.abs(alert.days_remaining)}d overdue` : `${alert.days_remaining}d remaining`
                                  ) : alert.type === 'LD_CAP_ALERT' ? 'LD Cap Warning' : ''}
                                </p>
                                {alert.id && (
                                  <button onClick={(e) => { e.stopPropagation(); timeBar.acknowledgeAlert(alert.id); }}
                                    className="text-[10px] px-2 py-0.5 rounded hover:opacity-80 transition-opacity"
                                    style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
                                    data-testid={`acknowledge-alert-${alert.id}`}>
                                    Dismiss
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <div className="px-3 py-2" style={{ borderTop: '1px solid var(--border-color)' }}>
                  <button onClick={() => navigate('/contracts')} className="w-full text-xs text-center" style={{ color: 'var(--accent-text)' }} data-testid="view-all-alerts">View all in Contracts module</button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 hover:opacity-80 transition-opacity duration-150">
                  <Avatar className="w-7 h-7" style={{ border: '1px solid var(--border-color)' }}>
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="text-xs font-bold" style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}>{initials}</AvatarFallback>
                  </Avatar>
                  <span className="text-xs hidden lg:block" style={{ color: 'var(--text-muted)' }}>{user?.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48" style={{ backgroundColor: 'var(--card-bg)', borderColor: 'var(--border-color)' }}>
                <div className="px-3 py-2">
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{user?.name}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{user?.email}</p>
                </div>
                <DropdownMenuSeparator style={{ backgroundColor: 'var(--border-color)' }} />
                {user?.platformRole === 'SUPERADMIN' && (
                  <DropdownMenuItem data-testid="nav-admin-menu" className="cursor-pointer" style={{ color: '#F26B1F' }} onClick={() => navigate('/admin')}>
                    <Shield className="w-3.5 h-3.5 mr-2" /> Admin Portal
                  </DropdownMenuItem>
                )}
                {!isTrial && (
                  <DropdownMenuItem data-testid="nav-settings-menu" className="cursor-pointer" style={{ color: 'var(--text-muted)' }} onClick={() => navigate('/settings')}>
                    <Settings className="w-3.5 h-3.5 mr-2" /> Settings
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="logout-btn" className="cursor-pointer text-red-500" onClick={handleLogout}>
                  <LogOut className="w-3.5 h-3.5 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="p-6">
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="min-w-0">
            {children}
          </motion.div>
        </main>
      </div>
      <GlobalModuleHelpFAB />
      <LinkJobsDock />
    </div>

  );
}

// Global floating "How to use this module" button — auto-detects module
// from the current URL and renders a ModuleHelp button if we have a guide.
function GlobalModuleHelpFAB() {
  const location = useLocation();
  const moduleKey = getModuleKeyFromPath(location.pathname);
  if (!moduleKey) return null;
  return (
    <div className="fixed bottom-5 right-5 z-40" data-testid="global-module-help-fab">
      <ModuleHelp moduleKey={moduleKey} compact />
    </div>
  );
}

function TrialBadge({ expiresAt }) {
  const ms = new Date(expiresAt).getTime() - Date.now();
  const expired = ms <= 0;
  const hours = Math.max(0, Math.floor(ms / 3600000));
  const mins  = Math.max(0, Math.floor((ms % 3600000) / 60000));
  return (
    <div data-testid="trial-badge" className="hidden md:inline-flex items-center gap-2 px-2.5 py-1 rounded text-[10px] tracking-[0.2em] uppercase font-medium border"
      style={{ borderColor: expired ? '#EF4444' : '#F59E0B', color: expired ? '#EF4444' : '#F59E0B', background: expired ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)' }}>
      {expired ? 'Trial expired' : <>Trial · {hours}h {mins}m left</>}
    </div>
  );
}
